import React from 'react';
import { ArrowRight, Plus, Minus } from 'lucide-react';
import { formatDateTime } from '../../utils/dateUtils';
import { capAuditText, labelForId } from '../../utils/auditDisplay';

/**
 * Format a field name for display (convert snake_case to Title Case).
 */
const formatFieldName = (fieldName) => {
  if (!fieldName) return '';
  return fieldName
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
};

/**
 * Get a human-readable label from an object (e.g., a worker or approval entry).
 */
const summarizeObject = (obj, referenceNames) => {
  if (!obj || typeof obj !== 'object') return String(obj);

  // Try common name fields first
  const nameFields = [
    'employee_name', 'approver_name', 'name', 'tester_name',
    'full_name', 'display_name', 'title', 'label',
    'original_filename', 'filename',
    'item', 'checklist_item', 'description',
    'equipment_name', 'equipment', 'location',
    'parameter', 'test_type',
  ];
  for (const field of nameFields) {
    if (obj[field]) return String(obj[field]);
  }

  // No name field, but an id we can resolve is better than a raw UUID.
  for (const [key, value] of Object.entries(obj)) {
    if (!key.endsWith('_id')) continue;
    const label = labelForId(value, referenceNames);
    if (label) return label;
  }

  // Fallback: show key-value pairs for non-id, non-internal fields
  const skipKeys = new Set(['id', '_id', 'created_at', 'updated_at', 'employee_id', 'approver_id', 'company_id', 'tenant_id']);
  const parts = Object.entries(obj)
    .filter(([k, v]) => !skipKeys.has(k) && v !== null && v !== undefined && v !== '')
    .slice(0, 3)
    .map(([k, v]) => {
      const label = k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      return `${label}: ${typeof v === 'object' ? JSON.stringify(v) : v}`;
    });
  return capAuditText(parts.length > 0 ? parts.join(', ') : JSON.stringify(obj));
};

/**
 * Format a value for display.
 */
const formatValue = (value, referenceNames) => {
  if (value === null || value === undefined) {
    return <span className="text-gray-400 italic">null</span>;
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-gray-400 italic">empty</span>;
    }
    // If array contains objects, summarize each one
    if (typeof value[0] === 'object' && value[0] !== null) {
      return (
        <ul className="list-disc list-inside space-y-0.5">
          {value.map((item, idx) => (
            <li key={idx} className="text-sm">{summarizeObject(item, referenceNames)}</li>
          ))}
        </ul>
      );
    }
    return capAuditText(value.join(', '));
  }

  if (typeof value === 'object') {
    return summarizeObject(value, referenceNames);
  }

  // Handle dates
  if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
    const formatted = formatDateTime(value);
    return formatted === '\u2014' ? value : formatted;
  }

  // A stored foreign key reads as a UUID. Show the record's name instead,
  // keeping the id in the tooltip so the entry is still traceable.
  const label = labelForId(value, referenceNames);
  if (label) {
    return <span title={value}>{label}</span>;
  }

  return capAuditText(String(value));
};

/**
 * DiffViewer Component
 *
 * Displays field-level changes between old and new values.
 *
 * @param {Object} props
 * @param {Object} props.changes - Object with field names as keys, {old_value, new_value} as values
 * @param {boolean} props.compact - Use compact display mode
 */
const DiffViewer = ({ changes, compact = false, referenceNames }) => {
  if (!changes || Object.keys(changes).length === 0) {
    return (
      <div className="text-gray-500 text-sm italic">
        No changes recorded
      </div>
    );
  }

  const changeEntries = Object.entries(changes);

  if (compact) {
    return (
      <div className="space-y-1">
        {changeEntries.slice(0, 3).map(([field, change]) => (
          <div key={field} className="text-sm">
            <span className="font-medium text-gray-700">{formatFieldName(field)}</span>
            {' changed'}
          </div>
        ))}
        {changeEntries.length > 3 && (
          <div className="text-sm text-gray-500">
            +{changeEntries.length - 3} more changes
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {changeEntries.map(([field, change]) => {
        const { old_value, new_value } = change;
        const isAdded = old_value === null || old_value === undefined;
        const isRemoved = new_value === null || new_value === undefined;

        return (
          <div key={field} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
            <div className="font-medium text-gray-800 mb-2 flex items-center gap-2">
              {isAdded && <Plus className="w-4 h-4 text-green-600" />}
              {isRemoved && <Minus className="w-4 h-4 text-red-600" />}
              {formatFieldName(field)}
            </div>

            <div className="flex items-start gap-3">
              {/* Old Value */}
              <div className={`flex-1 min-w-0 ${isAdded ? 'opacity-50' : ''}`}>
                <div className="text-xs text-gray-500 mb-1">Previous</div>
                <div className={`text-sm whitespace-pre-wrap break-words ${isAdded ? 'text-gray-400' : 'text-red-600 line-through'}`}>
                  {formatValue(old_value, referenceNames)}
                </div>
              </div>

              {/* Arrow */}
              <div className="flex items-center pt-5 shrink-0">
                <ArrowRight className="w-4 h-4 text-gray-400" />
              </div>

              {/* New Value */}
              <div className={`flex-1 min-w-0 ${isRemoved ? 'opacity-50' : ''}`}>
                <div className="text-xs text-gray-500 mb-1">New</div>
                <div className={`text-sm whitespace-pre-wrap break-words ${isRemoved ? 'text-gray-400' : 'text-green-600 font-medium'}`}>
                  {formatValue(new_value, referenceNames)}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * StatusChangeDiff Component
 *
 * Specialized view for status changes.
 */
export const StatusChangeDiff = ({ oldStatus, newStatus, reason }) => {
  return (
    <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="text-xs text-gray-500 mb-1">Previous Status</div>
          <div className="text-sm font-medium text-gray-700">{oldStatus || 'Unknown'}</div>
        </div>

        <ArrowRight className="w-5 h-5 text-blue-500" />

        <div className="flex-1">
          <div className="text-xs text-gray-500 mb-1">New Status</div>
          <div className="text-sm font-bold text-blue-700">{newStatus || 'Unknown'}</div>
        </div>
      </div>

      {reason && (
        <div className="mt-3 pt-3 border-t border-blue-200">
          <div className="text-xs text-gray-500 mb-1">Reason</div>
          <div className="text-sm text-gray-700">{reason}</div>
        </div>
      )}
    </div>
  );
};

export default DiffViewer;
